/**
 * The offline outbox.
 *
 * Every mutation the field app makes is written here first, with a client-generated
 * UUID, and only then sent. That UUID is the idempotency token: a replayed queue, a
 * retried request and a duplicate tap all collapse onto one server-side row.
 *
 * Plain IndexedDB — no dependency. The store itself lives in ./idb, underneath both
 * this and the API client, so signing out can clear the read cache without the two
 * of them importing each other.
 */

import { api, ApiError, session } from './api'
import { tx } from './idb'
import { photos } from './photos'

export { cache, setCacheOwner } from './idb'

export type OpType =
  | 'collection.capture'
  | 'collection.depot_in'
  | 'payment.request'
  | 'observation.capture'

export interface OutboxItem {
  clientUuid: string
  opType: OpType
  payload: Record<string, unknown>
  clientAt: string
  /** Shown in the queue list so a person can see what is waiting. */
  label: string
  sublabel?: string
  amount?: number
  weightKg?: number
  status: 'pending' | 'sending' | 'rejected'
  errorCode?: string
  errorDetail?: string
  attempts: number
}

// ---------------------------------------------------------------- outbox
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((fn) => fn())

export const outbox = {
  subscribe(fn: () => void) {
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  },

  async all(): Promise<OutboxItem[]> {
    const items = await tx<OutboxItem[]>('outbox', 'readonly', (s) => s.getAll())
    return items.sort((a, b) => a.clientAt.localeCompare(b.clientAt))
  },

  async count() {
    return (await this.all()).filter((i) => i.status !== 'rejected').length
  },

  async add(item: Omit<OutboxItem, 'status' | 'attempts' | 'clientAt'> & { clientAt?: string }) {
    const row: OutboxItem = {
      ...item,
      clientAt: item.clientAt ?? new Date().toISOString(),
      status: 'pending',
      attempts: 0,
    }
    await tx('outbox', 'readwrite', (s) => s.put(row))
    notify()
    return row
  },

  async remove(clientUuid: string) {
    await tx('outbox', 'readwrite', (s) => s.delete(clientUuid))
    notify()
  },

  async update(clientUuid: string, patch: Partial<OutboxItem>) {
    const row = await tx<OutboxItem | undefined>('outbox', 'readonly', (s) => s.get(clientUuid))
    if (!row) return
    await tx('outbox', 'readwrite', (s) => s.put({ ...row, ...patch }))
    notify()
  },

  /**
   * Pushes everything pending in one batch. Returns what the server did with it.
   * Rejected items stay in the queue with their reason so a person can act on them —
   * silently dropping a farmer's payment would be the worst possible failure mode.
   */
  async flush(): Promise<{ applied: number; replayed: number; rejected: number } | null> {
    if (!navigator.onLine || !session.isSignedIn) return null

    const pending = (await this.all()).filter((i) => i.status === 'pending')
    if (pending.length === 0) return { applied: 0, replayed: 0, rejected: 0 }

    // Photographs first. A capture made with no signal carries a `local:` reference
    // that means nothing to the server, so the pictures go up and the payloads are
    // rewritten to point at what they became. Sending the work first would record
    // evidence that does not exist - the exact failure this replaced.
    const uploaded = await photos.flush()
    if (Object.keys(uploaded).length > 0) {
      for (const item of pending) {
        let changed = false
        const payload = { ...item.payload }
        for (const [key, value] of Object.entries(payload)) {
          if (typeof value === 'string' && uploaded[value]) {
            payload[key] = uploaded[value]
            changed = true
          }
        }
        if (changed) {
          await this.update(item.clientUuid, { payload })
          item.payload = payload
        }
      }
    }

    for (const item of pending) await this.update(item.clientUuid, { status: 'sending' })

    try {
      const result = await api.post<{
        applied: number
        replayed: number
        rejected: number
        results: Array<{
          clientUuid: string
          status: string
          replay: boolean
          errorCode?: string
          errorDetail?: string
        }>
      }>('/sync', {
        deviceId: session.deviceId,
        items: pending.map((i) => ({
          clientUuid: i.clientUuid,
          opType: i.opType,
          payload: i.payload,
          clientAt: i.clientAt,
        })),
      })

      for (const r of result.results) {
        if (r.status === 'rejected' && !r.replay) {
          await this.update(r.clientUuid, {
            status: 'rejected',
            errorCode: r.errorCode,
            errorDetail: r.errorDetail,
          })
        } else {
          // Applied or replayed: the server has it, so the local copy can go.
          await this.remove(r.clientUuid)
        }
      }

      notify()
      return result
    } catch (e) {
      // Network failure: put everything back and try again later. Nothing is lost.
      for (const item of pending) {
        await this.update(item.clientUuid, {
          status: 'pending',
          attempts: item.attempts + 1,
          errorDetail: e instanceof ApiError ? e.message : undefined,
        })
      }
      return null
    }
  },

  /** Drops a rejected item once a person has decided it is not going to be fixed. */
  async discard(clientUuid: string) {
    await this.remove(clientUuid)
  },
}

/** Flush on reconnect and every 30 seconds while online. */
export function startSyncLoop() {
  const run = () => void outbox.flush()
  window.addEventListener('online', run)
  const timer = window.setInterval(() => {
    if (navigator.onLine) run()
  }, 30_000)
  run()
  return () => {
    window.removeEventListener('online', run)
    window.clearInterval(timer)
  }
}
